import React from "react";
import MuiAutocomplete from "@mui/material/Autocomplete";
import { styled } from "@mui/material/styles";
import TextField from "./TextField";

/**
 * Normalize options to { label, value }.
 * Accepts string[] or { label, value }[].
 */
const normalizeOptions = (options = []) =>
  options.map((opt) =>
    typeof opt === "string" ? { label: opt, value: opt } : opt
  );

/** Match shared dense TextField / select height + label centering. */
const DenseAutocomplete = styled(MuiAutocomplete)({
  "& .MuiOutlinedInput-root": {
    paddingTop: "0 !important",
    paddingBottom: "0 !important",
    paddingLeft: "0 !important",
    minHeight: 28,
  },
  "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
    padding: "5px 14px !important",
    fontSize: 12,
    minHeight: 18,
    height: "auto",
    color: "#999",
  },
  // Shared TextField uses marginTop:-8px for dense selects; that sits too high
  // on Autocomplete. Re-center the floating label when empty.
  "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
    marginTop: "0 !important",
    transform: "translate(14px, 8px) scale(1)",
  },
  "& .MuiInputLabel-root.MuiInputLabel-shrink": {
    marginTop: "0 !important",
  },
  "& .MuiAutocomplete-endAdornment": {
    right: 6,
    top: "50%",
    transform: "translateY(-50%)",
  },
  "& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator": {
    padding: 2,
  },
  "& .MuiSvgIcon-root": {
    fontSize: 18,
  },
});

/**
 * Controlled autocomplete: pick from suggestions or type a custom value.
 * Matches FMS report sensor type behavior (freeSolo).
 */
export default function FreeSoloAutocomplete({
  options = [],
  value = "",
  onChange,
  label,
  placeholder,
  fullWidth = true,
  disabled = false,
  clearOnBlur = false,
  ...rest
}) {
  const normalized = normalizeOptions(options);
  const current = value == null ? "" : String(value);

  return (
    <DenseAutocomplete
      freeSolo
      size="small"
      fullWidth={fullWidth}
      disabled={disabled}
      options={normalized}
      value={current}
      inputValue={current}
      clearOnBlur={clearOnBlur}
      selectOnFocus
      handleHomeEndKeys
      getOptionLabel={(option) => {
        if (typeof option === "string") {
          const match = normalized.find((o) => o.value === option);
          return match ? match.label : option;
        }
        return option?.label || option?.value || "";
      }}
      isOptionEqualToValue={(option, val) => {
        const optionValue =
          typeof option === "string" ? option : option?.value;
        const compare =
          typeof val === "string"
            ? val
            : val?.value != null
              ? String(val.value)
              : "";
        return optionValue === compare;
      }}
      filterOptions={(opts, state) => {
        const query = (state.inputValue || "").trim().toLowerCase();
        if (!query) return opts;
        return opts.filter((opt) => {
          const optLabel = (opt.label || "").toLowerCase();
          const val = (opt.value || "").toLowerCase();
          return optLabel.includes(query) || val.includes(query);
        });
      }}
      onChange={(_, newValue) => {
        if (newValue == null) {
          onChange("");
          return;
        }
        if (typeof newValue === "string") {
          onChange(newValue);
          return;
        }
        onChange(newValue.value ?? "");
      }}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === "input" || reason === "clear") {
          onChange(newInputValue);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          fullWidth={fullWidth}
          margin="dense"
          InputProps={{
            ...params.InputProps,
          }}
        />
      )}
      {...rest}
    />
  );
}
